import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight, ArrowLeft } from "lucide-react";
import { REVIEWED, CHRONOLOGICAL, byId } from "@/data/apparitions";
import { Reveal } from "@/components/landing/Reveal";

const TYPE_STYLE = {
  "Official shrine": "bg-marian text-ivory",
  Diocese: "bg-paleblue text-marian",
  "Holy See": "bg-gold text-ivory",
  Archive: "bg-charcoal/10 text-charcoal",
  Explanatory: "bg-ivory text-slateblue border border-marian/20",
  "Historical study": "bg-charcoal/10 text-charcoal",
  Prayer: "bg-paleblue text-marian",
  "Historical text": "bg-charcoal/10 text-charcoal",
  "Diocesan document": "bg-paleblue text-marian",
  "Church source": "bg-paleblue text-marian",
  Scripture: "bg-marian text-ivory",
};

export const SourceItem = ({ source, index, testIdPrefix }) => (
  <li className="flex min-w-0 items-start gap-4 rounded-xl border border-marian/10 bg-white/80 px-5 py-4" data-testid={`${testIdPrefix}-item-${index + 1}`}>
    <span className="mt-0.5 font-serif text-xl font-semibold text-gold/80">{String(index + 1).padStart(2, "0")}</span>
    <div className="min-w-0 flex-1">
      <span data-testid={`${testIdPrefix}-type-${index + 1}`} className={`inline-block rounded-full px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] ${TYPE_STYLE[source.type] || TYPE_STYLE.Explanatory}`}>
        {source.type}
      </span>
      <a
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testIdPrefix}-link-${index + 1}`}
        className="group mt-1.5 flex items-start gap-2 break-words text-sm font-medium leading-snug text-midnight transition-colors hover:text-marian"
      >
        {source.label}
        <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slateblue opacity-0 transition-opacity group-hover:opacity-100" />
      </a>
      {source.note && <p className="mt-2 text-xs leading-relaxed text-slateblue" data-testid={`${testIdPrefix}-note-${index + 1}`}>{source.note}</p>}
    </div>
  </li>
);

export default function SourcesPanel({ entry }) {
  const idx = CHRONOLOGICAL.findIndex((a) => a.id === entry.id);
  const prev = CHRONOLOGICAL[(idx - 1 + CHRONOLOGICAL.length) % CHRONOLOGICAL.length];
  const next = CHRONOLOGICAL[(idx + 1) % CHRONOLOGICAL.length];
  const related = entry.related.map(byId).filter(Boolean);

  return (
    <>
      <section className="bg-paleblue/50 py-16 sm:py-20" data-testid="sources-panel">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Source panel</p>
                <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-midnight sm:text-4xl">
                  Sources and Church documents
                </h2>
                <p className="mt-5 text-base leading-relaxed text-charcoal/85">
                  Church documents, shrine accounts, historical studies, and explanatory sources are
                  labelled separately. Reported encounters are attributed to their sources; original
                  spiritual commentary is distinguished from an official Church judgement.
                </p>
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slateblue" data-testid="last-reviewed">
                  Last reviewed: {entry.reviewed || REVIEWED}
                </p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slateblue/80">
                  Image: {entry.image.credit}
                </p>
              </div>
              <ol className="space-y-3">
                {entry.sources.map((s, i) => (
                  <SourceItem key={s.label} source={s} index={i} testIdPrefix={`source-panel-${entry.id}`} />
                ))}
              </ol>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="related-entries">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Related entries</p>
            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-midnight sm:text-4xl">Continue the pilgrimage</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-3">
            {related.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.08} className="h-full">
                <Link
                  to={`/apparitions/${r.id}`}
                  data-testid={`related-${r.id}`}
                  className="card-lift group flex h-full flex-col overflow-hidden rounded-2xl border border-marian/15 bg-white/90"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img src={r.image.url} alt={r.image.alt} loading="lazy" className={`h-full w-full object-cover ${r.image.position || "object-center"} transition-transform duration-700 group-hover:scale-105`} />
                    <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(16,42,67,0.6),transparent_60%)]" aria-hidden="true" />
                    <span className="absolute bottom-3 left-4 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/90">{r.country} · {r.year}</span>
                  </div>
                  <div className="flex flex-1 items-center justify-between gap-3 p-5">
                    <span className="font-serif text-xl font-semibold text-midnight">{r.title}</span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-gold transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="mt-14 grid gap-4 border-t border-marian/15 pt-8 sm:grid-cols-2">
            <Link to={`/apparitions/${prev.id}`} data-testid="prev-entry" className="group flex items-center gap-3 text-left">
              <ArrowLeft className="h-5 w-5 text-gold transition-transform duration-300 group-hover:-translate-x-1" />
              <span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slateblue">Earlier · {prev.year}</span>
                <span className="block font-serif text-xl font-semibold text-midnight group-hover:text-marian">{prev.title}</span>
              </span>
            </Link>
            <Link to={`/apparitions/${next.id}`} data-testid="next-entry" className="group flex items-center justify-end gap-3 text-right">
              <span>
                <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slateblue">Later · {next.year}</span>
                <span className="block font-serif text-xl font-semibold text-midnight group-hover:text-marian">{next.title}</span>
              </span>
              <ArrowRight className="h-5 w-5 text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
